import React from 'react'
import { Icon } from '../ui/Icon'

export interface MoreItem {
  label: string
  onSelect: () => void
  danger?: boolean
}

interface Props {
  // The actions, in groups; a line separates one group from the next. Empty
  // groups are left out.
  groups: MoreItem[][]
  disabled?: boolean
  // Told when the menu opens or closes, so the sheet's Escape leaves it alone.
  onOpenChange?: (open: boolean) => void
}

// MoreMenu is the ⋯ button beside a sheet's main action and the rarer actions
// it opens (appointment sheet review, 2026-10-15). The list opens upwards,
// over the details; where the sheet has no room above the button (a short
// sheet on a phone), it takes its own place above the main action instead and
// the sheet grows to hold it, so nothing scrolls out of sight. Arrow keys move
// through it, Escape closes it.
export default function MoreMenu({ groups, disabled, onOpenChange }: Props) {
  const [open, setOpen] = React.useState(false)
  const [inline, setInline] = React.useState(false)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)
  const listId = React.useId()
  const shown = groups.filter((g) => g.length > 0)

  const onOpenChangeRef = React.useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange
  React.useEffect(() => {
    onOpenChangeRef.current?.(open)
  }, [open])
  React.useEffect(() => () => onOpenChangeRef.current?.(false), [])

  const close = (refocus: boolean) => {
    setOpen(false)
    if (refocus) buttonRef.current?.focus()
  }

  React.useLayoutEffect(() => {
    const list = listRef.current
    const button = buttonRef.current
    if (!open || !list || !button) {
      setInline(false)
      return
    }
    const scroller = button.closest('.sheet-content')
    const top = scroller ? scroller.getBoundingClientRect().top : 0
    const fitsAbove = list.offsetHeight + 8 <= button.getBoundingClientRect().top - top
    setInline(!fitsAbove)
    list.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true })
  }, [open])

  React.useEffect(() => {
    if (open && inline) listRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [open, inline])

  // A tap anywhere else closes it.
  React.useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (!listRef.current?.contains(target) && !buttonRef.current?.contains(target)) close(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  const onKeyDown = (e: React.KeyboardEvent) => {
    const items = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])
    const at = items.indexOf(document.activeElement as HTMLElement)
    const move = (to: number) => {
      e.preventDefault()
      items[(to + items.length) % items.length]?.focus()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      close(true)
    } else if (e.key === 'ArrowDown') move(at + 1)
    else if (e.key === 'ArrowUp') move(at - 1)
    else if (e.key === 'Home') move(0)
    else if (e.key === 'End') move(items.length - 1)
    else if (e.key === 'Tab') close(false)
  }

  if (shown.length === 0) return null

  return (
    <div className="more-menu">
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-more"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="more" size={22} />
      </button>
      {open && (
        <div
          ref={listRef}
          id={listId}
          role="menu"
          aria-label="More actions"
          className={inline ? 'more-menu__list more-menu__list--inline' : 'more-menu__list'}
          onKeyDown={onKeyDown}
        >
          {shown.map((group, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div role="separator" className="more-menu__divider" />}
              {group.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  className={item.danger ? 'more-menu__item more-menu__item--danger' : 'more-menu__item'}
                  onClick={() => {
                    close(true)
                    item.onSelect()
                  }}
                >
                  {item.label}
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}
